import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { gameToPixel, getRadarImageUrl, hasMapTransform } from '../../utils/mapTransforms';
import './PlayerHeatmap.css';

// labelKey holds an i18n path resolved via t() at render (module constant can't call hooks).
const HEATMAP_TYPES = [
  { key: 'kill', labelKey: 'playerStats.kills' },
  { key: 'death', labelKey: 'playerStats.deaths' },
  { key: 'position', labelKey: 'playerStats.movement' },
];

// 'All' is translated (labelKey); T/CT are universal side codes kept literal.
const SIDE_OPTIONS = [
  { key: null, labelKey: 'playerStats.sideAll' },
  { key: 'T', label: 'T' },
  { key: 'CT', label: 'CT' },
];

const CANVAS_SIZE = 1024;

// Color palettes: array of [r, g, b] from cold to hot
const PALETTES = {
  kill: [
    [0, 0, 0],       // transparent (low)
    [0, 0, 180],     // blue
    [0, 180, 255],   // cyan
    [0, 255, 100],   // green
    [255, 255, 0],   // yellow
    [255, 100, 0],   // orange
    [255, 0, 0],     // red (hot)
  ],
  death: [
    [0, 0, 0],
    [40, 0, 80],
    [80, 0, 160],
    [140, 0, 220],
    [200, 0, 255],
    [255, 80, 255],
    [255, 180, 255],
  ],
  position: [
    [0, 0, 0],
    [0, 40, 40],
    [0, 100, 80],
    [0, 180, 120],
    [50, 220, 150],
    [120, 255, 180],
    [200, 255, 220],
  ],
};

function samplePalette(palette, t) {
  const n = palette.length - 1;
  const idx = t * n;
  const low = Math.floor(idx);
  const high = Math.min(low + 1, n);
  const frac = idx - low;
  return [
    Math.round(palette[low][0] + (palette[high][0] - palette[low][0]) * frac),
    Math.round(palette[low][1] + (palette[high][1] - palette[low][1]) * frac),
    Math.round(palette[low][2] + (palette[high][2] - palette[low][2]) * frac),
  ];
}

/**
 * Draw a gaussian heatmap on a canvas from an array of [x, y] points.
 * Uses grayscale accumulation with 'lighter' compositing, then colorizes.
 */
function drawHeatmap(ctx, points, width, height, type) {
  if (!points.length) return;

  const isMovement = type === 'position';
  const radius = isMovement ? 12 : 24;

  // Dynamic alpha: scale based on point count so few points are visible
  // and many points don't oversaturate
  const count = points.length;
  let pointAlpha;
  if (isMovement) {
    pointAlpha = Math.max(0.003, Math.min(0.05, 8 / count));
  } else {
    pointAlpha = Math.max(0.05, Math.min(0.6, 6 / count));
  }

  // Step 1: Accumulate intensity in grayscale using 'lighter' blending
  const heatCanvas = document.createElement('canvas');
  heatCanvas.width = width;
  heatCanvas.height = height;
  const hctx = heatCanvas.getContext('2d');
  hctx.clearRect(0, 0, width, height);
  hctx.globalCompositeOperation = 'lighter';

  for (const [px, py] of points) {
    if (px < -radius || py < -radius || px > width + radius || py > height + radius) continue;
    const gradient = hctx.createRadialGradient(px, py, 0, px, py, radius);
    gradient.addColorStop(0, `rgba(255, 255, 255, ${pointAlpha})`);
    gradient.addColorStop(0.4, `rgba(255, 255, 255, ${pointAlpha * 0.6})`);
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    hctx.fillStyle = gradient;
    hctx.beginPath();
    hctx.arc(px, py, radius, 0, Math.PI * 2);
    hctx.fill();
  }

  // Step 2: Read back intensity and colorize
  const imageData = hctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const palette = PALETTES[type] || PALETTES.kill;

  // Find max intensity for normalization
  let maxVal = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] > maxVal) maxVal = data[i]; // R channel (grayscale)
  }
  if (maxVal === 0) return;

  for (let i = 0; i < data.length; i += 4) {
    const intensity = data[i]; // R channel from grayscale
    if (intensity === 0) {
      data[i + 3] = 0;
      continue;
    }

    const t = Math.min(intensity / maxVal, 1);
    const [r, g, b] = samplePalette(palette, t);
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
    // Alpha: ramp up from 0 at low intensity to ~200 at high
    data[i + 3] = Math.min(Math.round(t * t * 300), 220);
  }

  hctx.putImageData(imageData, 0, 0);
  ctx.drawImage(heatCanvas, 0, 0);
}

/**
 * For kill/death with few points, also draw individual dots
 */
function drawDots(ctx, points, width, height, type) {
  if (!points.length || points.length > 200) return;
  if (type === 'position') return;

  const color = type === 'kill' ? 'rgba(255, 60, 60, 0.9)' : 'rgba(200, 80, 255, 0.9)';
  const borderColor = type === 'kill' ? 'rgba(255, 200, 200, 0.8)' : 'rgba(230, 180, 255, 0.8)';

  ctx.fillStyle = color;
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1.5;

  for (const [px, py] of points) {
    if (px < 0 || py < 0 || px > width || py > height) continue;
    ctx.beginPath();
    ctx.arc(px, py, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * Ritar heatmap-zonerna på canvasen: svaga ytor i fyra toner (grannzoner skiljer sig) för
 * automatiska zoner, eller rektangelkonturer för manuella. Etiketterna ritas separat
 * (`drawZoneLabels`) ovanpå heatmapen så att de alltid går att läsa.
 *
 * @param {CanvasRenderingContext2D} ctx Canvasens kontext.
 * @param {object} geo Svar från `/api/stats/heatmap/zones`.
 * @param {string} mapName Kartans tagg (nyckel i mapTransforms).
 * @param {number} size Canvasens storlek i pixlar.
 * @param {string[]} tints Fyra färger (CSS-variablerna `--hz-0`–`--hz-3`).
 * @param {string} line Konturfärg för manuella zoner.
 * @returns {void}
 */
function drawZones(ctx, geo, mapName, size, tints, line) {
  ctx.save();
  if (geo.source === 'auto') {
    ctx.globalAlpha = 0.22;
    geo.areas.forEach((poly, i) => {
      if (poly.length < 3) return;
      ctx.fillStyle = tints[(geo.zone_color || [])[geo.area_zone[i]] || 0];
      ctx.beginPath();
      poly.forEach(([x, y], k) => {
        const [px, py] = gameToPixel(x, y, mapName, size);
        if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.fill();
    });
  } else {
    ctx.strokeStyle = line;
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 6]);
    geo.zones.forEach((z) => {
      if (!z.rect) return;
      const [x0, y0, x1, y1] = z.rect;
      const [px0, py0] = gameToPixel(x0, y1, mapName, size);
      const [px1, py1] = gameToPixel(x1, y0, mapName, size);
      ctx.strokeRect(px0, py0, px1 - px0, py1 - py0);
    });
  }
  ctx.restore();
}

/**
 * Skriver zonnamnen vid zonernas tyngdpunkt, med mörk kant så att texten syns på heatmapen.
 *
 * @param {CanvasRenderingContext2D} ctx Canvasens kontext.
 * @param {object} geo Svar från `/api/stats/heatmap/zones`.
 * @param {string} mapName Kartans tagg.
 * @param {number} size Canvasens storlek i pixlar.
 * @returns {void}
 */
function drawZoneLabels(ctx, geo, mapName, size) {
  ctx.save();
  ctx.font = '600 22px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  geo.zones.forEach((z) => {
    const [px, py] = gameToPixel(z.cx, z.cy, mapName, size);
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.strokeText(z.name, px, py);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(z.name, px, py);
  });
  ctx.restore();
}

/**
 * PlayerHeatmap — spelarens kills/deaths/positioner på radarbilden, per karta.
 *
 * Har kartan zoner (manuella, annars automatiska ur nav-meshen) visas de som svaga
 * ytor med namn, och en lista under kartan räknar hur många av punkterna som ligger
 * i varje zon för valt filter (räknas på servern, höjdmedvetet).
 *
 * @param {{steamid64: string}} props Spelarens Steam-id.
 * @returns {JSX.Element|null} Heatmap-sektionen, eller null utan data.
 */
const PlayerHeatmap = ({ steamid64 }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [selectedType, setSelectedType] = useState('kill');
  const [selectedSide, setSelectedSide] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [radarImage, setRadarImage] = useState(null);
  const [zoneCounts, setZoneCounts] = useState(null);
  const [zoneGeo, setZoneGeo] = useState(null);
  const [showZones, setShowZones] = useState(true);

  // Fetch available maps
  useEffect(() => {
    fetch(`${API_URL}/api/stats/player/${steamid64}/heatmap/maps`)
      .then(res => res.json())
      .then(data => {
        const mapList = (data.maps || []).filter(m => m.map_name !== 'unknown');
        setMaps(mapList);
        // Prefer a known map with transforms
        const withTransform = mapList.find(m => hasMapTransform(m.map_name));
        setSelectedMap(withTransform ? withTransform.map_name : (mapList[0]?.map_name || null));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [steamid64]);

  // Load radar image when map changes
  useEffect(() => {
    if (!selectedMap) return;
    const url = getRadarImageUrl(selectedMap);
    if (!url) {
      setRadarImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setRadarImage(img);
    img.onerror = () => setRadarImage(null);
    img.src = url;
  }, [selectedMap]);

  // Zoner att rita (manuella eller automatiska) — en gång per karta.
  useEffect(() => {
    if (!selectedMap) return undefined;
    let cancelled = false;
    setZoneGeo(null);
    fetch(`${API_URL}/api/stats/heatmap/zones?map=${encodeURIComponent(selectedMap)}`)
      .then(res => res.json())
      .then(data => { if (!cancelled && !data.error) setZoneGeo(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedMap]);

  // Fetch position data when map/type/side changes
  useEffect(() => {
    if (!selectedMap) return;
    setLoading(true);

    let url = `${API_URL}/api/stats/player/${steamid64}/heatmap?map=${selectedMap}&type=${selectedType}`;
    if (selectedSide) url += `&side=${selectedSide}`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        setPositions(data.positions || []);
        setZoneCounts(data.zone_counts || null);
        setLoading(false);
      })
      .catch(() => {
        setPositions([]);
        setZoneCounts(null);
        setLoading(false);
      });
  }, [steamid64, selectedMap, selectedType, selectedSide]);

  const getDisplayName = (m) => m.display_name || m.map_name.replace(/^de_/, '').replace(/^\w/, c => c.toUpperCase());
  const selectedMapData = maps.find(m => m.map_name === selectedMap);
  const selectedDisplayName = selectedMapData ? getDisplayName(selectedMapData) : selectedMap;

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !selectedMap) return;

    const ctx = canvas.getContext('2d');
    const size = canvas.width;

    // Clear
    ctx.clearRect(0, 0, size, size);

    // Draw radar background
    if (radarImage) {
      ctx.drawImage(radarImage, 0, 0, size, size);
      // Darken for contrast
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fillRect(0, 0, size, size);
    } else {
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, size, size);
      if (!hasMapTransform(selectedMap)) {
        ctx.fillStyle = '#888';
        ctx.font = '20px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(t('playerStats.noRadar', { name: selectedDisplayName }), size / 2, size / 2 - 10);
        ctx.font = '14px sans-serif';
        ctx.fillText(t('playerStats.workshopMap'), size / 2, size / 2 + 15);
      }
    }

    const zonesVisible = showZones && zoneGeo && hasMapTransform(selectedMap);
    if (zonesVisible) {
      const style = getComputedStyle(canvas.parentElement);
      const tints = ['--hz-0', '--hz-1', '--hz-2', '--hz-3'].map((v) => style.getPropertyValue(v).trim() || '#888');
      drawZones(ctx, zoneGeo, selectedMap, size, tints, style.getPropertyValue('--hz-line').trim() || '#fff');
    }

    if (!positions.length) {
      if (zonesVisible) drawZoneLabels(ctx, zoneGeo, selectedMap, size);
      return;
    }

    // Convert game coords to pixel coords
    const pixelPoints = [];
    if (hasMapTransform(selectedMap)) {
      for (const pos of positions) {
        const [px, py] = gameToPixel(pos[0], pos[1], selectedMap, size);
        if (px >= -20 && py >= -20 && px <= size + 20 && py <= size + 20) {
          pixelPoints.push([px, py]);
        }
      }
    }

    if (pixelPoints.length > 0) {
      drawHeatmap(ctx, pixelPoints, size, size, selectedType);
      drawDots(ctx, pixelPoints, size, size, selectedType);
    }
    if (zonesVisible) drawZoneLabels(ctx, zoneGeo, selectedMap, size);
  }, [radarImage, positions, selectedMap, selectedType, selectedDisplayName, t, showZones, zoneGeo]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (maps.length === 0 && !loading) return null;

  return (
    <div className="heatmap-section">
      <h3>{t('playerStats.heatmap')}</h3>

      <div className="heatmap-controls">
        <div className="heatmap-control-group">
          <label>{t('playerStats.filterMap')}</label>
          <select
            value={selectedMap || ''}
            onChange={e => setSelectedMap(e.target.value)}
          >
            {maps.map(m => (
              <option key={m.map_name} value={m.map_name}>
                {getDisplayName(m)} ({t(m.match_count === 1 ? 'playerStats.matchesCountOne' : 'playerStats.matchesCountMany', { count: m.match_count })})
              </option>
            ))}
          </select>
        </div>

        <div className="heatmap-control-group">
          <label>{t('playerStats.filterType')}</label>
          <div className="heatmap-tabs">
            {HEATMAP_TYPES.map(tp => (
              <button
                key={tp.key}
                className={`heatmap-tab ${selectedType === tp.key ? 'active' : ''} type-${tp.key}`}
                onClick={() => setSelectedType(tp.key)}
              >
                {t(tp.labelKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="heatmap-control-group">
          <label>{t('playerStats.filterSide')}</label>
          <div className="heatmap-tabs">
            {SIDE_OPTIONS.map(s => (
              <button
                key={s.key || 'all'}
                className={`heatmap-tab ${selectedSide === s.key ? 'active' : ''} ${s.key ? `side-${s.key.toLowerCase()}` : ''}`}
                onClick={() => setSelectedSide(s.key)}
              >
                {s.labelKey ? t(s.labelKey) : s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="heatmap-canvas-wrapper">
        {loading && (
          <div className="heatmap-loading">
            <div className="loader"></div>
          </div>
        )}
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          className="heatmap-canvas"
        />
        {zoneGeo && hasMapTransform(selectedMap) && (
          <label className="heatmap-zone-toggle">
            <input type="checkbox" checked={showZones} onChange={(e) => setShowZones(e.target.checked)} />
            {t('playerStats.showZones')}
          </label>
        )}
        <div className="heatmap-info">
          {positions.length > 0
            ? t('playerStats.dataPoints', { count: positions.length })
            : selectedMap && !loading
              ? t('playerStats.noData')
              : ''}
          {selectedMapData && ` ${t(selectedMapData.match_count === 1 ? 'playerStats.fromMatchesOne' : 'playerStats.fromMatchesMany', { count: selectedMapData.match_count })}`}
        </div>
      </div>

      {zoneCounts && zoneCounts.zones.length > 0 && (
        <div className="heatmap-zones">
          <div className="heatmap-zones-header">
            <span className="heatmap-zones-title">{t('playerStats.zonesTitle')}</span>
            <span className="heatmap-zones-source">
              {t(zoneCounts.source === 'manual'
                ? 'playerStats.zonesManual'
                : zoneCounts.naming === 'places' ? 'playerStats.zonesPlaces' : 'playerStats.zonesAuto')}
            </span>
          </div>
          <ol className="heatmap-zones-list">
            {zoneCounts.zones.slice(0, 8).map((z) => {
              const pct = zoneCounts.total ? Math.round((z.count / zoneCounts.total) * 100) : 0;
              return (
                <li key={z.name} className="heatmap-zones-row">
                  <span className="heatmap-zones-name">{z.name}</span>
                  <progress className="heatmap-zones-bar" max={100} value={pct} />
                  <span className="heatmap-zones-value">{z.count} · {pct}%</span>
                </li>
              );
            })}
          </ol>
          {zoneCounts.outside > 0 && (
            <p className="heatmap-zones-note">{t('playerStats.zonesOutside', { count: zoneCounts.outside })}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerHeatmap;
